/**
 * Resolve the current effective user segment for navigation.
 *
 * Order of precedence:
 *   1. Quiz-state preview segment (admin "preview as another segment" flow).
 *   2. sessionStorage `hub_preview_segment` (SegmentGuard).
 *   3. Profile.user_segment (the user's real segment).
 *   4. Onboarding draft segment (anonymous visitors mid-signup).
 *   5. null → consumers should fall back to the generic nav.
 *
 * Returns { segment, isReady }. `isReady` is false while the auth/profile
 * context is still loading so nav consumers can render a skeleton instead
 * of flashing the fallback bar.
 */
import { useEffect, useState } from "react";
import { useProfileSafe } from "@/contexts/UserProfileContext";
import { loadQuizState } from "@/lib/quiz/quizStateMachine";
import { isSegment, type Segment } from "@/lib/navigation/segmentNavItems";

export interface EffectiveSegmentResult {
  segment: Segment | null;
  isReady: boolean;
}

function readDraftSegment(): Segment | null {
  try {
    const raw = localStorage.getItem("onboarding_draft");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const seg = parsed?.user_segment ?? parsed?.segment;
    return isSegment(seg) ? seg : null;
  } catch {
    return null;
  }
}

export function useEffectiveSegment(): Segment | null {
  // Backwards-compat: existing callers expect just the segment.
  return useEffectiveSegmentDetailed().segment;
}

export function useEffectiveSegmentDetailed(): EffectiveSegmentResult {
  const { profile, isLoading } = useProfileSafe();
  const [previewSegment, setPreviewSegment] = useState<Segment | null>(null);
  const [draftSegment, setDraftSegment] = useState<Segment | null>(null);

  useEffect(() => {
    const read = () => {
      try {
        let seg: unknown = null;
        try {
          seg = sessionStorage.getItem("hub_preview_segment");
        } catch {}
        if (!isSegment(seg)) {
          const state = loadQuizState();
          seg = (state as any)?.previewSegment ?? null;
        }
        setPreviewSegment(isSegment(seg) ? seg : null);
        setDraftSegment(readDraftSegment());
      } catch {
        setPreviewSegment(null);
      }
    };
    read();
    const handler = () => read();
    window.addEventListener("hub:preview-segment-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("hub:preview-segment-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  if (previewSegment) return { segment: previewSegment, isReady: true };

  const profileSeg = profile?.user_segment;
  if (isSegment(profileSeg)) return { segment: profileSeg, isReady: true };

  // No profile segment — fall back to onboarding draft for visitors mid-signup.
  if (draftSegment) return { segment: draftSegment, isReady: true };

  return { segment: null, isReady: !isLoading };
}
